import { apiFetch } from './client';

export type EdgeType =
  | 'related'
  | 'depends-on'
  | 'explains'
  | 'contradicts'
  | 'supersedes';

export interface GraphNode {
  id: string;
  title: string;
  domain: string;
  subdomain?: string;
  cluster?: string;
  quality: number;
  type: string;
  topics: string[];
}

export interface GraphEdge {
  source: string;
  target: string;
  type: EdgeType;
  confidence?: number;
}

export interface KnowledgeGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface MatureDomain {
  name: string;
  description: string;
  subdomains: string[];
  moc_id: string;
}

export interface EmergentCluster {
  name: string;
  member_count: number;
  cluster_page_id: string;
}

export interface KnowledgeRegistry {
  mature: MatureDomain[];
  emergent: EmergentCluster[];
}

export interface KnowledgeStats {
  total_pages: number;
  avg_quality: number;
  quality_distribution: {
    stub: number;
    drafted: number;
    mature: number;
    canonical: number;
  };
  pages_by_domain: Record<string, number>;
  pages_by_type: Record<string, number>;
  edge_count_by_type: Record<string, number>;
  mature_domain_count: number;
  emergent_cluster_count: number;
}

export interface LintContradiction {
  page_a_id: string;
  page_a_title: string;
  page_b_id: string;
  page_b_title: string;
  confidence: number;
}

export interface LintOrphan {
  page_id: string;
  title: string;
}

export interface LintStale {
  page_id: string;
  title: string;
  days_since_update: number;
}

export interface LintStuckStub {
  page_id: string;
  title: string;
  days_stuck: number;
  quality: number;
}

export interface LintBrokenSource {
  page_id: string;
  page_title: string;
  source_ref: string;
  source_title: string;
}

export interface LintReport {
  pages_scanned: number;
  contradictions: LintContradiction[];
  orphans: LintOrphan[];
  stale: LintStale[];
  stuck_stubs: LintStuckStub[];
  broken_sources: LintBrokenSource[];
}

export interface PageDetail {
  id: string;
  title: string;
  meta: Record<string, unknown>;
  content_preview: string;
  edges: Array<{ target: string; type?: string; confidence?: number }>;
}

export const fetchGraph = (): Promise<KnowledgeGraph> =>
  apiFetch<KnowledgeGraph>('/api/knowledge/graph');

export const fetchRegistry = (): Promise<KnowledgeRegistry> =>
  apiFetch<KnowledgeRegistry>('/api/knowledge/registry');

export const fetchStats = (): Promise<KnowledgeStats> =>
  apiFetch<KnowledgeStats>('/api/knowledge/stats');

export const fetchLint = (): Promise<LintReport> =>
  apiFetch<LintReport>('/api/knowledge/lint');

export const fetchPage = (id: string): Promise<PageDetail> =>
  apiFetch<PageDetail>(`/api/knowledge/page/${encodeURIComponent(id)}`);
